<template>
  <component
    :is="component"
    class="flex items-start space-x-2.5 cursor-pointer transition text-light-800 dark:text-dark-300 rounded-2xl pt-1.5 pb-1.5 px-2"
    :class="[
      notificationQueue.readAt ? '' : 'is-unread',
      notificationQueue.priority === 'high' && !notificationQueue.readAt ? 'bg-tiger-100 dark:bg-tiger-600/20' : '',
    ]"
    :to="content?.to ?? undefined"
    @click.stop="onClick"
  >  
    <div class="size-10 shrink-0 bg-cover relative flex items-center justify-center" :class="[ users.length > 0 ? '' : 'bg-light-200 dark:bg-dark-800 rounded-full' ]" v-if="users">
      <div class="size-10 bg-contain rounded-md relative overflow-hidden" v-if="organizations[0]?.logo">
        <img :src="organizations[0].logo.getSize('s')" alt="" class="w-full h-full object-cover" />
      </div>
      <UserPicture
        v-else-if="users.length === 1 && users[0]"
        size="sm+"
        is-hide-badges
        is-link
        :user="users[0]"
      />
      <template v-else-if="users.length > 1">
        <UserPicture
          v-for="(user, i) in users.slice(0, 2)"
          :user="user"
          :key="user._id"
          border-classes="outline outline-[3px] outline-light-50 dark:outline-dark-920"
          is-hide-badges
          is-link
          class="!absolute"
          :class="[
            i === 0 ? 'left-0 top-0' : 'right-0 bottom-0 z-10'
          ]"
          size="xs"
        />
      </template>
      <IconBase v-else name="cute/info-circle-fill" />
    </div>
    
    <div class="grow min-h-10 flex flex-col justify-center" v-if="content">
      <div class="text-base leading-tight" v-html="content.title" />
      <div class="text-base leading-tight" v-if="content.content">
        {{ content.content }}
      </div>

      <p class="text-2xs font-medium mt-0.5 space-x-1.5 flex items-center" :class="[ notificationQueue.readAt ? 'text-light-800 dark:text-dark-300' : 'text-light-950 dark:text-white' ]">
        <span
          class="size-1 rounded-full bg-royal-600 dark:bg-royal-400"
          v-if="!notificationQueue.readAt"
        />

        <span class="grow">{{ dayjs(notificationQueue.updatedAt).fromNow() }}</span>
      </p>
    </div>

    <div
      class="bg-cover bg-center rounded-lg shrink-0 size-10"
      :style="{ backgroundImage: `url(${content.image})` }"
      v-if="content?.image"
    />
  </component>
</template>

<script lang="ts" setup>
import UserStore from '@/store/user'
import UsersStore from '@/store/users'
import EventsStore from '@/store/events'
import OrganizationStore from '@/store/organization'
import GroupsStore from '@/store/groups'
import PostsStore from '@/store/posts'
import { NotificationQueue } from '@/classes/Notification'
import dayjs from 'dayjs'

const props = defineProps<{
  notificationQueue: NotificationQueue
}>()

const { notificationQueue } = toRefs(props)
const notifications = computed(() => notificationQueue.value.notifications ?? [])

const eventsStore = EventsStore()
const userStore = UserStore()
const usersStore = UsersStore()
const organizationStore = OrganizationStore()
const groupsStore = GroupsStore()
const postsStore = PostsStore()

const userIds = computed(() => notifications.value.map(n => n.content?.user).filter(u => u) as string[])

const users = computed(() => usersStore.getItems(userIds.value).sort((a, b) => userIds.value.indexOf(a._id) - userIds.value.indexOf(b._id)))

const events = computed(() => eventsStore.getItems(notifications.value.map(n => n.content?.event).filter(e => e) as string[]))

const groups = computed(() => groupsStore.getItems(notifications.value.map(n => n.content?.group).filter(g => g) as string[]))

const posts = computed(() => postsStore.getItems(notifications.value.map(n => n.content?.post).filter(p => p) as string[]))

const postParent = computed(() => posts.value[0]?.parent ? postsStore.getItem(posts.value[0].parent) : null)

const organizations = computed(() => organizationStore.getItems(notifications.value.map(n => n.content?.organization).filter(o => o) as string[]))

const content = computed(() => {
  const result = {
    to: null as null | RouteLocationRaw,
    image: '',
    title: '',
    content: '',
    link: ''
  }

  try {
    const names = getNameList(users.value.map(u => u.name), {
      total: userIds.value.length,
      and: useNuxtApp().$i18n.t('common.and'),
      others: useNuxtApp().$i18n.t('common.others')
    })

    if (notificationQueue.value.type === 'follow-new') {
      if (!users.value[0]) throw new Error('missing-names')

      result.title = useNuxtApp().$i18n.t('notifications.follow-new.' + (users.value.length > 1 ? 'plural' : 'singular'), { names })

      result.to = users.value.length === 1 ? { query: { user: users.value[0]?.username } } : { query: { followers: 'followers' } }
    } else if (notificationQueue.value.type === 'event-friend') {
      if (!events.value[0]) throw new Error('missing-event')

      result.title = useNuxtApp().$i18n.t('notifications.event-friend.' + (users.value.length > 1 ? 'plural' : 'singular'), { names, event: events.value[0]?.title })

      result.to = { name: 'app-events-slug', params: { slug: events.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.type === 'event-friend-organize') {
      if (!events.value[0]) throw new Error('missing-event')

      result.title = useNuxtApp().$i18n.t('notifications.event-friend-organize.singular', { names, event: events.value[0]?.title })

      result.to = { name: 'app-events-slug', params: { slug: events.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.type === 'post-answer') {
      if (!postParent.value) throw new Error('missing-post-parent')

      result.title = useNuxtApp().$i18n.t('notifications.post-answer.' + (users.value.length > 1 ? 'plural' : 'singular'), { names, post: truncate(stripTags(postParent.value?.title ?? postParent.value?.content ?? ''), 5) })

      if (users.value.length === 1) {
        result.content = truncate(stripTags(posts.value[0]?.content), 20)
      }

      result.to = { query: { post: posts.value[0]?.parent } }
      result.image = postParent.value?.medias?.[0]?.getSize('s') ?? ''
    } else if (notificationQueue.value.type === 'post-mention') {
      if (!postParent.value) throw new Error('missing-post-parent')

      result.title = useNuxtApp().$i18n.t('notifications.post-mention.' + (users.value.length > 1 ? 'plural' : 'singular'), { names, post: truncate(stripTags(postParent.value?.title ?? postParent.value?.content ?? ''), 5) })

      if (users.value.length === 1) {
        result.content = truncate(stripTags(posts.value[0]?.content), 20)
      }

      result.to = { query: { post: posts.value[0]?.parent } }
      result.image = postParent.value?.medias?.[0]?.getSize('s') ?? ''
    } else if (notificationQueue.value.type === 'post-reactions') {
      if (!posts.value[0]) throw new Error('missing-post')

      result.title = useNuxtApp().$i18n.t('notifications.post-reactions.' + (users.value.length > 1 ? 'plural' : 'singular'), { names, post: truncate(stripTags(posts.value[0]?.title ?? posts.value[0]?.content ?? ''), 5) })

      result.to = { query: { post: posts.value[0] } }
      result.image = posts.value[0]?.medias?.[0]?.getSize('s') ?? ''
    } else if (notificationQueue.value.type === 'event-organization-organize') {
      if (!events.value[0] || !organizations.value[0]) throw new Error('missing-event')

      result.title = useNuxtApp().$i18n.t('notifications.event-organization-organize.singular', { organization: organizations.value[0]?.name, event: events.value[0]?.title })

      result.to = { name: 'app-events-slug', params: { slug: events.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.type === 'event-attendee') {
      if (!events.value[0]) throw new Error('missing-event')

      result.title = useNuxtApp().$i18n.t('notifications.event-attendee.' + (users.value.length > 1 ? 'plural' : 'singular'), { names, event: events.value[0]?.title })

      result.to = { name: 'app-events-slug', params: { slug: events.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.type === 'event-validated') {
      if (!events.value[0]) throw new Error('missing-event')

      result.title = useNuxtApp().$i18n.t('notifications.event-validated.' + (users.value.length > 1 ? 'plural' : 'singular'), { names, event: events.value[0]?.title })

      result.to = { name: 'app-events-slug', params: { slug: events.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.type === 'event-reviewed') {
      if (!events.value[0]) throw new Error('missing-event')

      result.title = useNuxtApp().$i18n.t('notifications.event-reviewed.' + (users.value.length > 1 ? 'plural' : 'singular'), { names, event: events.value[0]?.title })

      result.to = { name: 'app-events-slug', params: { slug: events.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.type === 'event-end') {
      if (!events.value[0]) throw new Error('missing-event')

      result.title = useNuxtApp().$i18n.t('notifications.event-end.singular', { event: events.value[0]?.title })

      result.to = { name: 'app-events-slug', params: { slug: events.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.type === 'event-cancelled') {
      if (!events.value[0]) throw new Error('missing-event')

      result.title = useNuxtApp().$i18n.t('notifications.event-cancelled.singular', { event: events.value[0]?.title })

      result.to = { name: 'app-events-slug', params: { slug: events.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.type === 'event-waiting-over') {
      if (!events.value[0]) throw new Error('missing-event')

      result.title = useNuxtApp().$i18n.t('notifications.event-waiting-over.singular', { event: events.value[0]?.title })

      result.to = { name: 'app-events-slug', params: { slug: events.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.type === 'group-request') {
      if (!groups.value[0]) throw new Error('missing-group')

      result.title = useNuxtApp().$i18n.t('notifications.group-request.' + (users.value.length > 1 ? 'plural' : 'singular'), { names, group: groups.value[0]?.name })

      result.to = { name: 'app-groups-slug', params: { slug: groups.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.type === 'group-request-accepted') {
      if (!groups.value[0]) throw new Error('missing-group')

      result.title = useNuxtApp().$i18n.t('notifications.group-request-accepted.singular', { group: groups.value[0]?.name })

      result.to = { name: 'app-groups-slug', params: { slug: groups.value[0]?.slug ?? '' } }
    } else if (notificationQueue.value.formatted?.title) {
      result.title = notificationQueue.value.formatted.title
      result.content = notificationQueue.value.formatted.content ?? ''

      if (notificationQueue.value.formatted.link) {
        let link = notificationQueue.value.formatted.link

        link = link.replace('https://vibes.lgbt', '')
        link = link.replace(useRuntimeConfig().public.APP_URL, '')
        link = link.replace('https://app.vibes.lgbt', '')
        link = link.replace('/app', '')

        result.to = link
      }
    } else {
      result.title = notificationQueue.value.type
    }

    if (events.value.length > 0) {
      result.image = events.value.find(e => e.image)?.image?.getSize('s') ?? ''
    }
  } catch (e) {}
    
  return result?.title ? result : null
})

const component = computed(() => {
  if (content.value?.to) {
    return resolveComponent('NuxtLink')
  } else {
    return 'div'
  }
})

const onClick = async () => {
  userStore.readNotifications({
    type: 'read',
    notifications: [ notificationQueue.value._id ]
  })
}
</script>

<style lang="scss" scoped>

.is-unread {
  :deep(b) {
    @apply text-light-950 dark:text-white !important;
    font-variation-settings: 'wght' 800;
  }

  :deep(i) {
    font-style: normal;
    @apply text-royal-800 dark:text-royal-400 !important;
    font-variation-settings: 'wght' 800;
  }
}

:deep(b) {
  @apply text-light-800 dark:text-dark-300;
  font-variation-settings: 'wght' 520;
}

:deep(i) {
  font-style: normal;
  @apply text-light-800 dark:text-dark-300;
  font-variation-settings: 'wght' 520;
}
</style>
